<template>
  <div>
    <a-page-header title="执行计划管理">
      <template #extra>
        <a-button type="primary" @click="handleCreate">
          <template #icon><PlusOutlined /></template>
          创建执行计划
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <a-form layout="inline" :model="searchForm" style="margin-bottom: 16px">
        <a-form-item label="状态">
          <a-select v-model:value="searchForm.status" style="width: 150px" allowClear>
            <a-select-option value="pending">待执行</a-select-option>
            <a-select-option value="executing">执行中</a-select-option>
            <a-select-option value="completed">已完成</a-select-option>
            <a-select-option value="cancelled">已取消</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="handleSearch">查询</a-button>
          <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>

      <a-table
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.status)">
              {{ getStatusText(record.status) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'current_step'">
            <span v-if="record.current_step_order">
              步骤 {{ record.current_step_order }}
            </span>
            <span v-else>-</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button type="link" @click="handleView(record)">查看详情</a-button>
            <a-button v-if="record.status === 'executing' || record.status === 'pending'" type="link" danger @click="handleCancel(record)">取消</a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 执行计划详情弹窗 -->
    <a-modal
      v-model:open="detailModalVisible"
      title="执行计划详情"
      width="1200px"
      :footer="null"
    >
      <div v-if="planDetail">
        <a-descriptions :column="3" bordered style="margin-bottom: 16px">
          <a-descriptions-item label="计划编号">{{ planDetail.plan_code }}</a-descriptions-item>
          <a-descriptions-item label="方案名称">{{ planDetail.scheme_name }}</a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag :color="getStatusColor(planDetail.status)">
              {{ getStatusText(planDetail.status) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="当前步骤">
            <span v-if="planDetail.current_step_order">步骤 {{ planDetail.current_step_order }}</span>
            <span v-else>-</span>
          </a-descriptions-item>
          <a-descriptions-item label="创建时间">{{ planDetail.create_time }}</a-descriptions-item>
          <a-descriptions-item label="开始时间">{{ planDetail.start_time || '-' }}</a-descriptions-item>
        </a-descriptions>

        <a-tabs v-model:activeKey="activeTab">
          <a-tab-pane key="steps" tab="执行步骤">
            <a-table
              :columns="stepColumns"
              :data-source="planSteps"
              :pagination="false"
              row-key="step_order"
              size="small"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'step_name'">
                  <div>
                    <div>{{ record.step.step_name }}</div>
                    <div v-if="record.sub_steps && record.sub_steps.length > 0" style="margin-top: 8px; padding-left: 20px; color: #666">
                      <div v-for="sub in record.sub_steps" :key="sub.id" style="margin: 4px 0">
                        • {{ sub.step_name }}
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else-if="column.key === 'is_required'">
                  <a-tag :color="record.is_required === 1 ? 'red' : 'default'">
                    {{ record.is_required === 1 ? '必选' : '可选' }}
                  </a-tag>
                </template>
              </template>
            </a-table>
          </a-tab-pane>
          <a-tab-pane key="logs" tab="执行日志">
            <a-table
              :columns="logColumns"
              :data-source="planLogs"
              :loading="logLoading"
              :pagination="false"
              row-key="id"
              size="small"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'status'">
                  <a-tag :color="getLogStatusColor(record.status)">
                    {{ getLogStatusText(record.status) }}
                  </a-tag>
                </template>
                <template v-else-if="column.key === 'knowledge_count'">
                  {{ record.knowledge_count || 0 }}
                </template>
                <template v-else-if="column.key === 'action'">
                  <a-button type="link" size="small" @click="handleViewLog(record)">查看详情</a-button>
                </template>
              </template>
            </a-table>
          </a-tab-pane>
          <a-tab-pane key="summary" tab="执行摘要">
            <a-descriptions :column="2" bordered v-if="logSummary">
              <a-descriptions-item label="总步骤数">{{ logSummary.total_steps }}</a-descriptions-item>
              <a-descriptions-item label="已完成">{{ logSummary.completed_steps }}</a-descriptions-item>
              <a-descriptions-item label="失败">{{ logSummary.failed_steps }}</a-descriptions-item>
              <a-descriptions-item label="跳过">{{ logSummary.skipped_steps }}</a-descriptions-item>
              <a-descriptions-item label="待执行">{{ logSummary.pending_steps }}</a-descriptions-item>
              <a-descriptions-item label="总知识数">{{ logSummary.total_knowledge }}</a-descriptions-item>
              <a-descriptions-item label="已执行知识">{{ logSummary.executed_knowledge }}</a-descriptions-item>
              <a-descriptions-item label="完成进度">
                <a-progress :percent="logSummary.progress" />
              </a-descriptions-item>
            </a-descriptions>
          </a-tab-pane>
        </a-tabs>
      </div>
    </a-modal>

    <!-- 日志详情弹窗 -->
    <a-modal
      v-model:open="logDetailModalVisible"
      title="执行日志详情"
      width="800px"
      :footer="null"
    >
      <div v-if="logDetail">
        <a-descriptions :column="1" bordered>
          <a-descriptions-item label="步骤名称">{{ logDetail.step_name }}</a-descriptions-item>
          <a-descriptions-item label="执行状态">
            <a-tag :color="getLogStatusColor(logDetail.status)">
              {{ getLogStatusText(logDetail.status) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="关联知识数">{{ logDetail.knowledge_count || 0 }}</a-descriptions-item>
          <a-descriptions-item label="开始时间">{{ logDetail.start_time || '-' }}</a-descriptions-item>
          <a-descriptions-item label="结束时间">{{ logDetail.end_time || '-' }}</a-descriptions-item>
          <a-descriptions-item label="执行耗时">{{ logDetail.duration ? logDetail.duration + 'ms' : '-' }}</a-descriptions-item>
          <a-descriptions-item label="错误信息" v-if="logDetail.error_message">
            <a-alert :message="logDetail.error_message" type="error" />
          </a-descriptions-item>
        </a-descriptions>

        <a-collapse style="margin-top: 16px">
          <a-collapse-panel key="request" header="请求数据">
            <pre>{{ JSON.stringify(logDetail.request_data, null, 2) }}</pre>
          </a-collapse-panel>
          <a-collapse-panel key="response" header="响应数据">
            <pre>{{ JSON.stringify(logDetail.response_data, null, 2) }}</pre>
          </a-collapse-panel>
        </a-collapse>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { PlusOutlined } from '@ant-design/icons-vue';
import { getCallPlans, createCallPlan, getCallPlanById, cancelCallPlan, getCallPlanLogs, getCallPlanLogSummary, getCallPlanLog } from '../api/callPlan';

export default {
  name: 'CallPlan',
  components: {
    PlusOutlined
  },
  setup() {
    const loading = ref(false);
    const dataSource = ref([]);
    const searchForm = reactive({
      status: undefined
    });
    const pagination = reactive({
      current: 1,
      pageSize: 20,
      total: 0
    });

    const detailModalVisible = ref(false);
    const planDetail = ref(null);
    const planSteps = ref([]);
    const planLogs = ref([]);
    const logLoading = ref(false);
    const logSummary = ref(null);
    const activeTab = ref('steps');

    const logDetailModalVisible = ref(false);
    const logDetail = ref(null);

    const columns = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
      { title: '计划编号', dataIndex: 'plan_code', key: 'plan_code', width: 180 },
      { title: '方案名称', dataIndex: 'scheme_name', key: 'scheme_name' },
      { title: '状态', key: 'status', width: 100 },
      { title: '当前步骤', key: 'current_step', width: 100 },
      { title: '创建时间', dataIndex: 'create_time', key: 'create_time', width: 180 },
      { title: '操作', key: 'action', width: 200, fixed: 'right' }
    ];

    const stepColumns = [
      { title: '顺序', dataIndex: 'step_order', key: 'step_order', width: 80 },
      { title: '步骤名称', key: 'step_name' },
      { title: '是否必选', key: 'is_required', width: 100 }
    ];

    const logColumns = [
      { title: '步骤名称', dataIndex: 'step_name', key: 'step_name' },
      { title: '步骤顺序', dataIndex: 'step_order', key: 'step_order', width: 100 },
      { title: '状态', key: 'status', width: 100 },
      { title: '知识数', key: 'knowledge_count', width: 100 },
      { title: '开始时间', dataIndex: 'start_time', key: 'start_time', width: 180 },
      { title: '操作', key: 'action', width: 100 }
    ];

    const getStatusColor = (status) => {
      const colors = {
        pending: 'orange',
        executing: 'blue',
        completed: 'green',
        cancelled: 'red'
      };
      return colors[status] || 'default';
    };

    const getStatusText = (status) => {
      const texts = {
        pending: '待执行',
        executing: '执行中',
        completed: '已完成',
        cancelled: '已取消'
      };
      return texts[status] || status;
    };

    const getLogStatusColor = (status) => {
      const colors = {
        pending: 'orange',
        executing: 'blue',
        success: 'green',
        failed: 'red',
        skipped: 'default'
      };
      return colors[status] || 'default';
    };

    const getLogStatusText = (status) => {
      const texts = {
        pending: '待执行',
        executing: '执行中',
        success: '成功',
        failed: '失败',
        skipped: '跳过'
      };
      return texts[status] || status;
    };

    const loadData = async () => {
      loading.value = true;
      try {
        const params = {
          page: pagination.current,
          pageSize: pagination.pageSize,
          status: searchForm.status
        };
        const res = await getCallPlans(params);
        dataSource.value = res.data.items || [];
        pagination.total = res.data.total || 0;
      } catch (error) {
        message.error('加载数据失败');
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      pagination.current = 1;
      loadData();
    };

    const handleReset = () => {
      searchForm.status = undefined;
      handleSearch();
    };

    const handleTableChange = (pag) => {
      pagination.current = pag.current;
      pagination.pageSize = pag.pageSize;
      loadData();
    };

    const handleCreate = async () => {
      try {
        await createCallPlan();
        message.success('创建成功');
        loadData();
      } catch (error) {
        message.error('创建失败');
      }
    };

    const handleView = async (record) => {
      detailModalVisible.value = true;
      activeTab.value = 'steps';
      
      try {
        const res = await getCallPlanById(record.id);
        planDetail.value = res.data;
        
        if (res.data.scheme_snapshot && res.data.scheme_snapshot.steps) {
          planSteps.value = res.data.scheme_snapshot.steps;
        }

        // 加载日志
        await loadLogs(record.id);
        await loadLogSummary(record.id);
      } catch (error) {
        message.error('加载详情失败');
      }
    };

    const loadLogs = async (planId) => {
      logLoading.value = true;
      try {
        const res = await getCallPlanLogs(planId);
        planLogs.value = res.data || [];
      } catch (error) {
        message.error('加载日志失败');
      } finally {
        logLoading.value = false;
      }
    };

    const loadLogSummary = async (planId) => {
      try {
        const res = await getCallPlanLogSummary(planId);
        logSummary.value = res.data;
      } catch (error) {
        console.error('加载摘要失败:', error);
      }
    };

    const handleCancel = async (record) => {
      try {
        await cancelCallPlan(record.id);
        message.success('取消成功');
        loadData();
      } catch (error) {
        message.error('取消失败');
      }
    };

    const handleViewLog = async (record) => {
      logDetailModalVisible.value = true;
      try {
        const res = await getCallPlanLog(record.plan_id, record.id);
        logDetail.value = res.data;
      } catch (error) {
        message.error('加载日志详情失败');
      }
    };

    onMounted(() => {
      loadData();
    });

    return {
      loading,
      dataSource,
      searchForm,
      pagination,
      columns,
      detailModalVisible,
      planDetail,
      planSteps,
      planLogs,
      logLoading,
      logSummary,
      activeTab,
      stepColumns,
      logColumns,
      logDetailModalVisible,
      logDetail,
      getStatusColor,
      getStatusText,
      getLogStatusColor,
      getLogStatusText,
      handleSearch,
      handleReset,
      handleTableChange,
      handleCreate,
      handleView,
      handleCancel,
      handleViewLog
    };
  }
};
</script>

